import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  Link,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { TbArrowLeft, TbCheck, TbChevronRight, TbRefresh } from "react-icons/tb";
import { CrossborderCopyButton } from "../../../Crossborder/components/CrossborderCopyButton";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../../Hooks/useBaasPlatformCreateAccess";
import { useBaasPageLoading } from "../../../../../Hooks/useBaasPageLoading";
import {
  fetchBaasAccountById,
  mergeBalancesWithCurrencyOpenings,
} from "../../../../../Services/baasAccountsListApi";
import {
  approveBaasAccountProposal,
  resolveBaasAccountExternalId,
} from "../../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceDetail } from "../../../../../Services/baasInstancesApi";
import { fetchIbAccountCurrencyViews } from "../../../../../Services/baasSaldoExtratoApi";
import { BaasAccountCurrencyBalanceCards } from "./BaasAccountCurrencyBalanceCards";
import { BaasAccountDetailSkeleton } from "../../components/BaasSkeleton";
import { BaasProposalSituationCard } from "../../components/BaasProposalSituationBadge";
import {
  canAccountTransact,
  resolveAccountOperationalStatus,
  resolveProposalSituation,
} from "../../../../../Utils/baasProposalSituation";
import {
  canShowBaasAccountLifecycleActions,
  hasBaasBankingRails,
  isBaasCardsOnlyInstance,
} from "../../baasCapabilities";
import {
  BAAS_ACCOUNTS_PATH,
  BAAS_ACCOUNT_TAB_SEGMENTS,
  BAAS_ROOT_PATH,
  buildBaasAccountDetailPath,
  buildBaasAccountPixPath,
} from "../../constants";
import { BaasAccountDetailQuickActions } from "./BaasAccountDetailQuickActions";
import { BaasAccountInfoPanel } from "./BaasAccountInfoPanel";
import { BaasAccountInboundPixOriginLockPanel } from "./BaasAccountInboundPixOriginLockPanel";
import { BaasAccountOverBalancePanel } from "./BaasAccountOverBalancePanel";
import { BaasAccountOpeningEventsPanel } from "./BaasAccountOpeningEventsPanel";
import { BaasAccountLimitsPanel } from "./BaasAccountLimitsPanel";
import { BaasAccountAccessPanel } from "./BaasAccountAccessPanel";
import { BaasAccountPixKeysModal } from "./BaasAccountPixKeysModal";
import {
  isBaasExitRailVirtualAccount,
  isVirtualInstanceShellAccount,
} from "../../../../../Services/baasVirtualShells";
import { BaasAccountRelatedVirtualAccountsPanel } from "./BaasAccountRelatedVirtualAccountsPanel";
import { BaasVirtualAccountCreateModal } from "./BaasVirtualAccountCreateModal";
import {
  isContasInstanceRootAccount,
  isContasOperationShellAccount,
} from "./BaasEntityVirtualAccountsPanel";
import { BaasAccountStatusBadge } from "./BaasAccountStatusBadge";
import { BaasExtratoPage } from "../../Extrato/BaasExtratoPage";

const DETAIL_TABS = [
  { id: "extrato", labelKey: "baas.accounts.detail.tabs.extrato" },
  { id: "limites", labelKey: "baas.accounts.detail.tabs.limites" },
  { id: "conta", labelKey: "baas.accounts.detail.tabs.conta" },
  { id: "acessos", labelKey: "baas.accounts.detail.tabs.acessos" },
];

const formatAccountTypeLabel = (type) => {
  if (!type) return null;
  const normalized = String(type).trim().toLowerCase();
  if (normalized === "otc" || normalized === "otc_virtual") {
    return intl.get("baas.accounts.type.wallet") || "WALLET";
  }
  const key = `baas.accounts.type.${type}`;
  const label = intl.get(key);
  return label && label !== key ? label : type;
};

const resolveAccountTitle = (account, accountId) => {
  const alias = String(account?.alias || account?.name || "").trim();
  if (alias) return alias;
  const entityName = String(account?.entityName || "").trim();
  if (entityName) return entityName;
  return formatAccountTypeLabel(account?.type) || account?.id || accountId;
};

const resolveActiveTab = (pathname, accountId) => {
  const base = buildBaasAccountDetailPath(accountId);
  if (!pathname.startsWith(base)) return "extrato";
  const rest = pathname.slice(base.length).replace(/^\//, "");
  const segment = rest.split("/")[0] || "";
  if (segment === BAAS_ACCOUNT_TAB_SEGMENTS.limites) return "limites";
  if (segment === BAAS_ACCOUNT_TAB_SEGMENTS.acessos) return "acessos";
  if (
    segment === BAAS_ACCOUNT_TAB_SEGMENTS.conta
    || segment === BAAS_ACCOUNT_TAB_SEGMENTS.configuracao
    || segment === BAAS_ACCOUNT_TAB_SEGMENTS.metadata
  ) {
    return "conta";
  }
  return "extrato";
};

/**
 * Shared Contas / Root account detail surface (header, balances, operational actions, tabs).
 *
 * @param {object} props
 * @param {string} props.accountId
 * @param {boolean} [props.embedded] — Root embeds without Contas route chrome / redirects
 * @param {boolean} [props.showBackLink]
 * @param {string} [props.backTo]
 * @param {string} [props.eyebrowKey] — optional intl key override for eyebrow
 */
export const BaasAccountDetailPanel = ({
  accountId,
  embedded = false,
  showBackLink = true,
  backTo = BAAS_ACCOUNTS_PATH,
  eyebrowKey = null,
}) => {
  const match = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const { selectedInstance, opsInstance, selectAccount, clearPeerOpsPin } = useBaasInstance();
  const apiInstance = opsInstance || selectedInstance;
  const pageLoading = useBaasPageLoading(accountId);
  const { can, capabilities, loading: capabilitiesLoading } = useBaasCapabilities();
  const { isValutConfigAdmin } = useBaasPlatformCreateAccess();
  const allowed = can("accounts.view");
  const canViewPix = can("processing.pix.view");
  const canCreateAccount = can("accounts.create");
  const canLifecycleActions = canShowBaasAccountLifecycleActions({
    embedded,
    isValutConfigAdmin,
    capabilities,
    canCreateAccount,
  });
  const [account, setAccount] = useState(null);
  const [proposalSituation, setProposalSituation] = useState(null);
  const [currencyViews, setCurrencyViews] = useState([]);
  const [accountLoading, setAccountLoading] = useState(true);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [balancesRefreshKey, setBalancesRefreshKey] = useState(0);
  const [pixKeysOpen, setPixKeysOpen] = useState(false);
  const [virtualCreateOpen, setVirtualCreateOpen] = useState(false);
  const [allowManualApproval, setAllowManualApproval] = useState(false);
  const [approvingAccount, setApprovingAccount] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [embeddedTab, setEmbeddedTab] = useState("extrato");

  const routedTab = useMemo(
    () => (embedded ? null : resolveActiveTab(location.pathname, accountId)),
    [embedded, location.pathname, accountId],
  );
  const currentTab = embedded ? embeddedTab : routedTab;

  const handleBackToList = useCallback(() => {
    if (embedded) return;
    clearPeerOpsPin?.();
  }, [embedded, clearPeerOpsPin]);

  const clearPixKeysQuery = () => {
    if (embedded) return;
    const params = new URLSearchParams(location.search);
    if (!params.has("pixKeys") && !location.state?.openPixKeys) return;
    params.delete("pixKeys");
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : "",
      state: { ...(location.state || {}), openPixKeys: undefined },
    });
  };

  const handleClosePixKeys = () => {
    setPixKeysOpen(false);
    clearPixKeysQuery();
  };

  useEffect(() => {
    if (accountId) {
      selectAccount?.(accountId);
    }
  }, [accountId, selectAccount]);

  useEffect(() => {
    if (embedded) {
      setEmbeddedTab("extrato");
    }
  }, [accountId, embedded]);

  useEffect(() => {
    if (embedded) return;

    const params = new URLSearchParams(location.search);
    const wantsPixKeys =
      params.get("pixKeys") === "1" || Boolean(location.state?.openPixKeys);

    if (!wantsPixKeys) return;

    if (!canViewPix) {
      setPixKeysOpen(false);
      params.delete("pixKeys");
      const search = params.toString();
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        state: { ...(location.state || {}), openPixKeys: undefined },
      });
      return;
    }

    setPixKeysOpen(true);
  }, [
    embedded,
    location.search,
    location.state,
    location.pathname,
    canViewPix,
    history,
  ]);

  useEffect(() => {
    if (!canViewPix) setPixKeysOpen(false);
  }, [canViewPix]);

  useEffect(() => {
    let cancelled = false;

    if (!apiInstance) {
      setAllowManualApproval(false);
      return undefined;
    }

    setAllowManualApproval(Boolean(apiInstance.allowSkipKyc));

    (async () => {
      try {
        const detail = await fetchBaasInstanceDetail(apiInstance);
        if (!cancelled) {
          setAllowManualApproval(Boolean(detail?.allowSkipKyc));
        }
      } catch {
        if (!cancelled) setAllowManualApproval(Boolean(apiInstance.allowSkipKyc));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiInstance]);

  useEffect(() => {
    let cancelled = false;

    if (!apiInstance || !accountId) {
      setAccount(null);
      setProposalSituation(null);
      setAccountLoading(false);
      return undefined;
    }

    setAccountLoading(true);
    (async () => {
      try {
        const next = await fetchBaasAccountById(apiInstance, accountId);
        if (cancelled) return;
        setAccount(next);
        setCurrencyViews(
          mergeBalancesWithCurrencyOpenings(
            next?.balances ?? [],
            next?.currencyOpenings ?? [],
            next?.banking ?? null,
          ),
        );

        // Prefer list/DB proposalSituation + Account flags from evento_conta
        // webhooks. Never auto-call PaySmart on Contas enter. Live GET is only
        // Consultar proposta (and approve, which refreshes via doProposalProcessing).
        setProposalSituation(
          resolveProposalSituation(next?.proposalSituation || null, next) || null,
        );
      } catch {
        if (!cancelled) {
          setAccount(null);
          setProposalSituation(null);
        }
      } finally {
        if (!cancelled) setAccountLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiInstance, accountId, reloadToken]);

  const handleApproveAccount = useCallback(async () => {
    const accountExternalId = resolveBaasAccountExternalId(account);
    if (!apiInstance || !account?.entityId || !accountExternalId || approvingAccount) return;

    setApprovingAccount(true);
    setApproveError("");
    try {
      const next = await approveBaasAccountProposal(
        apiInstance,
        account.entityId,
        accountExternalId,
        { proposalId: account.proposalId },
      );
      if (next?.situation) {
        setProposalSituation(next.situation);
      }
      setReloadToken((token) => token + 1);
    } catch (err) {
      const message =
        err?.response?.data?.detail
        || err?.detail
        || err?.response?.data?.error
        || err?.code
        || err?.message
        || intl.get("baas.accounts.quickActions.approveAccountFailed");
      setApproveError(message);
    } finally {
      setApprovingAccount(false);
    }
  }, [apiInstance, account, approvingAccount]);

  useEffect(() => {
    let cancelled = false;

    if (!apiInstance || !accountId || !account) {
      return undefined;
    }

    setBalancesLoading(true);
    (async () => {
      try {
        const next = await fetchIbAccountCurrencyViews(
          apiInstance,
          accountId,
          account.banking ?? null,
          account.currencyOpenings ?? [],
        );
        if (!cancelled && Array.isArray(next) && next.length) {
          setCurrencyViews(next);
        }
      } catch {
        /* keep account.balances / list openings snapshot */
      } finally {
        if (!cancelled) setBalancesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [apiInstance, accountId, account, balancesRefreshKey]);

  if (pageLoading || accountLoading || capabilitiesLoading) {
    return <BaasAccountDetailSkeleton />;
  }

  const shellsOnlyMode = isBaasCardsOnlyInstance(capabilities);

  if (!hasBaasBankingRails(capabilities) && !shellsOnlyMode) {
    if (embedded) {
      return (
        <div className="app-card px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      );
    }
    return <Redirect to="/baas/inicio" />;
  }

  if (!allowed) {
    if (embedded) {
      return (
        <div className="app-card px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      );
    }
    return <Redirect to={shellsOnlyMode ? BAAS_ROOT_PATH : BAAS_ACCOUNTS_PATH} />;
  }

  if (!account) {
    if (embedded) {
      return (
        <div className="app-card px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.root.shellDetail.notFound")}
        </div>
      );
    }
    return (
      <div className="app-card space-y-4 px-6 py-10 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.notFound")}
        </p>
        <Link
          to={backTo || BAAS_ACCOUNTS_PATH}
          onClick={handleBackToList}
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#a66f1d] dark:text-brand-gold-mid"
        >
          {intl.get("baas.accounts.detail.back")}
        </Link>
      </div>
    );
  }

  const isVirtualAccount = isBaasExitRailVirtualAccount(account);
  const isShellAccount =
    isVirtualInstanceShellAccount(account) || isContasOperationShellAccount(account);

  if (shellsOnlyMode && !isShellAccount && !embedded) {
    return <Redirect to={BAAS_ROOT_PATH} />;
  }

  const displayProposalSituation = proposalSituation || account.proposalSituation || null;
  const displayStatus =
    resolveAccountOperationalStatus({
      proposalSituation: displayProposalSituation,
      verificationStatus: account.verificationStatus,
      status: account.status,
      documentPending: account.documentPending,
      antiFraudAnalysis: account.antiFraudAnalysis,
      statusName: account.statusName,
      raw: account.raw || account,
    }).status || account.status;
  const accountCanTransact = canAccountTransact({
    ...account,
    status: displayStatus,
    proposalSituation: displayProposalSituation,
  });
  const showApproveAccount =
    !isVirtualAccount
    && (allowManualApproval || isValutConfigAdmin)
    && canLifecycleActions
    && Boolean(account.entityId)
    && (displayStatus === "pending"
      || displayStatus === "blocked"
      || displayStatus === "suspended");

  const resolvedEyebrowKey =
    eyebrowKey
    || (isVirtualAccount
      ? "baas.accounts.detail.virtualEyebrow"
      : embedded
        ? "baas.root.shellDetail.eyebrow"
        : "baas.accounts.detail.eyebrow");

  const tabCurrencies = currencyViews.map((view) => view.currency).filter(Boolean);
  const accountTitle = resolveAccountTitle(account, accountId);
  const accountIdValue = account.id || accountId;
  const titleIsAccountId = accountTitle === accountIdValue;
  const typeLabel = formatAccountTypeLabel(account.type);
  const entityTo = account.entityId
    ? `/baas/entidades?entityId=${encodeURIComponent(account.entityId)}`
    : "/baas/entidades";
  const TitleTag = embedded ? "h2" : "h1";
  const titleClassName = `min-w-0 max-w-full ${titleIsAccountId ? "break-all font-mono" : "break-words"} ${
    embedded ? "text-xl sm:text-2xl" : "text-2xl"
  } font-bold text-brand-dark dark:text-gray-100`;
  const titleIsEntityName =
    Boolean(account.entityName)
    && accountTitle === String(account.entityName).trim();
  const canCreateVirtual = canLifecycleActions && !isVirtualAccount && !isShellAccount;

  const relatedAccountsPanel = (
    <BaasAccountRelatedVirtualAccountsPanel
      operationAccounts={account.operationAccounts}
      virtualAccounts={account.virtualAccounts}
      showOperationShells={
        // Root page already renders the operation-shell cards above the
        // embedded detail — listing them again here duplicates the grid.
        !embedded
        && isContasInstanceRootAccount(account, {
          rootAccountId: apiInstance?.rootAccountId,
        })
      }
      showVirtualAccounts={!isVirtualAccount && !isShellAccount}
      onCreateVirtualClick={
        canLifecycleActions && !isShellAccount
          ? () => setVirtualCreateOpen(true)
          : null
      }
    />
  );

  const renderAccountTab = () => (
    <div className="space-y-6">
      <BaasAccountInfoPanel account={account} />
      {relatedAccountsPanel}
      {!isVirtualAccount && !isShellAccount ? (
        <BaasAccountOverBalancePanel accountId={account.id || accountId} />
      ) : null}
      <BaasAccountInboundPixOriginLockPanel
        accountId={account.id || accountId}
        account={account}
      />
      {!isVirtualAccount && !isShellAccount ? (
        <BaasAccountOpeningEventsPanel
          instance={apiInstance}
          accountId={account.id || accountId}
        />
      ) : null}
    </div>
  );

  const renderTabBody = () => {
    if (currentTab === "limites") {
      return <BaasAccountLimitsPanel accountId={accountId} />;
    }
    if (currentTab === "acessos") {
      return <BaasAccountAccessPanel accountId={accountId} account={account} />;
    }
    if (currentTab === "conta") {
      return renderAccountTab();
    }
    return (
      <BaasExtratoPage
        embedded
        accountId={accountId}
        accountCurrencies={tabCurrencies}
      />
    );
  };

  return (
    <div className={`baas-account-detail min-w-0 ${embedded ? "space-y-4" : "space-y-6"}`}>
      <header className={`flex flex-col lg:flex-row lg:items-start ${showApproveAccount ? "lg:justify-between" : ""} ${embedded ? "gap-3" : "gap-4"}`}>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {showBackLink ? (
              <Link
                to={backTo}
                onClick={handleBackToList}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
              >
                <TbArrowLeft className="h-3.5 w-3.5" aria-hidden />
                {intl.get("baas.accounts.detail.back")}
              </Link>
            ) : null}
            <p className="text-xs font-semibold uppercase tracking-wider text-[#e7a73f]">
              {intl.get(resolvedEyebrowKey)}
            </p>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 sm:gap-3">
            <TitleTag className={titleClassName}>
              {titleIsEntityName && account.entityId ? (
                <Link to={entityTo} className="transition hover:underline">
                  {accountTitle}
                </Link>
              ) : (
                accountTitle
              )}
            </TitleTag>
            {isVirtualAccount ? (
              <span className="inline-flex items-center rounded-lg border border-[#e7a73f]/45 bg-[#e7a73f]/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#a66f1d] dark:border-brand-gold-mid/50 dark:bg-brand-gold-mid/15 dark:text-brand-gold-mid">
                {intl.get("baas.accounts.detail.virtualBadge")}
              </span>
            ) : typeLabel && typeLabel !== accountTitle ? (
              <span className="inline-flex items-center rounded-lg bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-300">
                {typeLabel}
              </span>
            ) : null}
            <BaasAccountStatusBadge status={displayStatus} />
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1">
            <span className="break-all font-mono text-xs text-gray-500 dark:text-gray-400">
              {accountIdValue}
            </span>
            <CrossborderCopyButton
              value={accountIdValue}
              ariaLabel={intl.get("baas.accounts.metadata.copyValue", {
                field: intl.get("baas.accounts.detail.accountId"),
              })}
            />
          </div>
          {!titleIsEntityName && account.entityId ? (
            <Link
              to={entityTo}
              className="mt-1 inline-flex max-w-full items-center gap-1 text-sm font-semibold text-[#e7a73f] transition hover:underline"
            >
              <span className="min-w-0 truncate">
                {account.entityName || intl.get("baas.accounts.quickActions.viewEntity")}
              </span>
              <TbChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            </Link>
          ) : null}
        </div>

        {showApproveAccount ? (
          <div className="flex flex-wrap items-center gap-2 self-start">
            <button
              type="button"
              className="btn-primary w-auto shrink-0 !text-xs"
              disabled={approvingAccount}
              title={intl.get("baas.accounts.quickActions.approveAccountHint")}
              onClick={handleApproveAccount}
            >
              <TbCheck className="h-4 w-4" />
              {approvingAccount
                ? intl.get("baas.accounts.quickActions.approveAccountLoading")
                : intl.get("baas.accounts.quickActions.approveAccount")}
            </button>
          </div>
        ) : null}
      </header>

      {displayProposalSituation && displayProposalSituation.tone !== "approved" ? (
        <BaasProposalSituationCard situation={displayProposalSituation} />
      ) : null}

      {approveError ? (
        <p
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
        >
          {approveError}
        </p>
      ) : null}

      {!accountCanTransact && !isVirtualAccount ? (
        <p
          role="status"
          className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300"
        >
          {intl.get("baas.accounts.quickActions.awaitingApprovalHint")}
        </p>
      ) : null}

      {isVirtualAccount ? (
        <p
          role="status"
          className="rounded-xl border border-[#e7a73f]/35 bg-[#e7a73f]/10 px-4 py-3 text-sm text-[#7a5418] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid"
        >
          {intl.get("baas.accounts.detail.virtualLimitedBanner")}
        </p>
      ) : null}

      <BaasAccountDetailQuickActions
        accountId={account.id}
        entityId={account.entityId}
        embedded={embedded}
        account={{
          ...account,
          status: displayStatus,
          proposalSituation: displayProposalSituation,
          canTransact: accountCanTransact,
        }}
        onPixKeysClick={() => setPixKeysOpen(true)}
        onCreateVirtualClick={canCreateVirtual ? () => setVirtualCreateOpen(true) : null}
        onProposalLoaded={(proposal) => {
          if (proposal?.situation) {
            setProposalSituation(proposal.situation);
          }
        }}
        onLifecycleChanged={() => setReloadToken((token) => token + 1)}
      />

      <section className="baas-account-detail__balances space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <p className="dashboard-section-label mb-0">
            {intl.get("baas.accounts.detail.balancesTitle")}
          </p>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition hover:text-brand-dark disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
            onClick={() => setBalancesRefreshKey((value) => value + 1)}
            disabled={balancesLoading}
          >
            <TbRefresh className="h-3.5 w-3.5" aria-hidden />
            {intl.get("baas.saldo.refresh")}
          </button>
        </div>
        <BaasAccountCurrencyBalanceCards
          currencyViews={currencyViews}
          loading={balancesLoading}
        />
      </section>

      <div
        className="extract-view-tabs extract-view-tabs--inline border-b border-gray-100 dark:border-brand-dark-border"
        role="tablist"
        aria-label={intl.get("baas.accounts.detail.title")}
      >
        {DETAIL_TABS.map((tab) => {
          const isActive = currentTab === tab.id;
          if (embedded) {
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`extract-view-tab link-no-zoom ${isActive ? "extract-view-tab--active" : ""}`}
                onClick={() => setEmbeddedTab(tab.id)}
              >
                {intl.get(tab.labelKey)}
              </button>
            );
          }
          const to = buildBaasAccountDetailPath(accountId, tab.id);
          return (
            <Link
              key={tab.id}
              to={to}
              role="tab"
              aria-selected={isActive}
              className={`extract-view-tab link-no-zoom ${isActive ? "extract-view-tab--active" : ""}`}
            >
              {intl.get(tab.labelKey)}
            </Link>
          );
        })}
      </div>

      {embedded ? (
        renderTabBody()
      ) : (
        <Switch>
          <Route exact path={match.path}>
            <BaasExtratoPage
              embedded
              accountId={accountId}
              accountCurrencies={tabCurrencies}
            />
          </Route>
          <Route exact path={`${match.path}/limites`}>
            <BaasAccountLimitsPanel accountId={accountId} />
          </Route>
          <Route exact path={`${match.path}/conta`}>
            {renderAccountTab()}
          </Route>
          <Route exact path={`${match.path}/acessos`}>
            <BaasAccountAccessPanel accountId={accountId} account={account} />
          </Route>
          <Route exact path={`${match.path}/configuracao`}>
            <Redirect to={buildBaasAccountDetailPath(accountId, "conta")} />
          </Route>
          <Route exact path={`${match.path}/metadata`}>
            <Redirect to={buildBaasAccountDetailPath(accountId, "conta")} />
          </Route>
          <Route exact path={`${match.path}/extrato`}>
            <Redirect to={buildBaasAccountDetailPath(accountId)} />
          </Route>
          <Route exact path={`${match.path}/saldo`}>
            <Redirect to={buildBaasAccountDetailPath(accountId)} />
          </Route>
          <Route exact path={`${match.path}/pix`}>
            <Redirect
              to={
                canViewPix
                  ? buildBaasAccountPixPath(accountId)
                  : buildBaasAccountDetailPath(accountId)
              }
            />
          </Route>
          <Redirect to={buildBaasAccountDetailPath(accountId)} />
        </Switch>
      )}

      {canViewPix ? (
        <BaasAccountPixKeysModal
          open={pixKeysOpen}
          onClose={handleClosePixKeys}
          accountId={account.id}
          account={account}
        />
      ) : null}

      {!isVirtualAccount && !isShellAccount && canLifecycleActions ? (
        <BaasVirtualAccountCreateModal
          open={virtualCreateOpen}
          onClose={() => setVirtualCreateOpen(false)}
          initialEntityId={account.entityId || ""}
          onCreated={() => {
            setVirtualCreateOpen(false);
            setBalancesRefreshKey((value) => value + 1);
          }}
        />
      ) : null}
    </div>
  );
};

export default BaasAccountDetailPanel;
